import React, { useState, useEffect } from 'react'
import compose from 'recompose/compose'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import { connect as connectRedux } from 'react-redux'
import { List, Map, fromJS } from 'immutable'
import _get from 'lodash/get'
import _times from 'lodash/times'
import { useQuery } from '@apollo/client'
import { getBoundActions } from '../../../actions/index'
import { getPrimary } from '../../../services/languages/index'
import { connect as connectStaticText } from '../../StaticText/connect'
import { EN } from '../../../services/languages/constants'
import {
  URL_COMING_SOON,
  URL_COMING_SOON_DE,
  URL_COMING_SOON_ES,
  URL_COMING_SOON_FR,
  URL_AI_CHAT,
} from '../../../services/url/constants'
import { GET_RECOMMENDED } from '../../../services/content/query'
import { CONTENT_IMAGE_LIST, getFormattedContentData } from '../../../services/content/index'
import { updateUpstreamContextFromObject } from '../../../services/upstream-context/index'
import { STYLES } from '../../Row.v2/index'
import RowV3 from '../../Row.v3/index'
import TileV2Graphql from '../../Tile.v2/Tile.v2Graphql/index'
import Button, { TYPES } from '../../Button/index'
import ChevronRightIcon from '../../Icon/icons/ChevronRight/index'
import Link from '../../Link/index'
import HighlightMessage from '../../HighlightMessage/index'
import { H3, H5, HEADING_TYPES } from '../../Heading/index'
import {
  CANCEL_FLOW_COMING_SOON_CLICKED_GA4_EVENT,
  CANCEL_FLOW_AI_GUIDE_PROMPT_CLICKED_GA4_EVENT,
  AI_CANCEL_MODAL_VIEWED_GA4_EVENT,
  AI_CANCEL_MODAL_ENGAGED_GA4_EVENT,
} from '../../../services/event-tracking/index'
import { isAccountCancelConfirmPage } from '../../../services/url/index'

const COMING_SOON_LINKS = {
  en: URL_COMING_SOON,
  de: URL_COMING_SOON_DE,
  fr: URL_COMING_SOON_FR,
  es: URL_COMING_SOON_ES,
}

const LEGACY_IMAGE_PATHS = {
  en: () => import('./img/cancel_dissatisfied.jpg'),
  es: () => import('./img/cancel_dissatisfied_es.jpg'),
  de: () => import('./img/cancel_dissatisfied_de.jpg'),
  fr: () => import('./img/cancel_dissatisfied_fr.jpg'),
}

function AccountCancelDissatisfiedLegacy (props) {
  const {
    staticText,
    userLanguage = EN,
    setEventDefault,
  } = props
  const [currentImage, setCurrentImage] = useState(null)
  const comingSoonLink = COMING_SOON_LINKS[userLanguage] || URL_COMING_SOON

  useEffect(() => {
    const loadImage = async () => {
      try {
        const { default: src } = await LEGACY_IMAGE_PATHS[userLanguage]()
        setCurrentImage(src)
      } catch (error) {
        // Silently handle image loading errors
      }
    }
    loadImage()
  }, [userLanguage])

  const onClickSeeWhatIsComing = () => {
    setEventDefault(CANCEL_FLOW_COMING_SOON_CLICKED_GA4_EVENT)
  }

  const highlightMessageContent = (
    <>
      {staticText.getIn(['data', 'haveYou1'])}
      <Link
        to={comingSoonLink}
        className="account-cancel-dissatisfied__coming--bold"
        onClick={onClickSeeWhatIsComing}
      >
        {staticText.getIn(['data', 'haveYou2'])}
      </Link>
      {staticText.getIn(['data', 'haveYou3'])}
    </>
  )

  return (
    <HighlightMessage
      className="account-cancel-dissatisfied"
      title={staticText.getIn(['data', 'newContent'])}
      content={highlightMessageContent}
      buttonText={staticText.getIn(['data', 'seeWhatIsComing'])}
      buttonURL={comingSoonLink}
      buttonOnClick={onClickSeeWhatIsComing}
      imgUrl={currentImage}
      imgLink={comingSoonLink}
      onImgLinkClick={onClickSeeWhatIsComing}
    />
  )
}

function AccountCancelDissatisfiedAiGuide (props) {
  const {
    staticText,
    oraclePersonalityId,
    setAiGa4Event,
    setEventDefault,
  } = props
  useEffect(() => {
    setAiGa4Event(AI_CANCEL_MODAL_VIEWED_GA4_EVENT)
  }, [])

  const onAiGuidePromptClicked = (uiElement) => () => {
    setAiGa4Event(AI_CANCEL_MODAL_ENGAGED_GA4_EVENT.mergeDeep({
      ui_element: uiElement,
    }))
    setEventDefault(CANCEL_FLOW_AI_GUIDE_PROMPT_CLICKED_GA4_EVENT)
  }

  return (
    <div className="account-cancel-dissatisfied__highlight-message-container account-cancel-dissatisfied__ai-guide">
      <H5 className="account-cancel-dissatisfied__highlight-message-title">
        {staticText.getIn(['data', 'aiGuideTitle'])}
      </H5>
      <p className="account-cancel-dissatisfied__highlight-message-content">
        {staticText.getIn(['data', 'aiGuideRecommendations'])}
      </p>
      <Button
        type={TYPES.PRIMARY}
        onClick={onAiGuidePromptClicked('AI Guide Show Content')}
        className="account-cancel-dissatisfied__highlight-message-button"
        url={URL_AI_CHAT}
        query={{
          prompt: staticText.getIn(['data', 'aiGuideShowContentPrompt']),
          oraclePersonalityId,
        }}
      >
        {staticText.getIn(['data', 'aiGuideTryIt2'])}
        <ChevronRightIcon />
      </Button>
    </div>
  )
}

AccountCancelDissatisfiedAiGuide.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  setAiGa4Event: PropTypes.func.isRequired,
  setEventDefault: PropTypes.func.isRequired,
  oraclePersonalityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
}

const RECOMMENDED_TILE_LIMIT = 12

function AccountCancelDissatisfiedEnhanced (props) {
  const {
    staticText,
    userLanguage = EN,
    setEventDefault,
    auth,
    upstreamContext,
    currentPath,
    oraclePersonalityId,
    setAiGa4Event,
  } = props
  const comingSoonLink = COMING_SOON_LINKS[userLanguage] || URL_COMING_SOON
  const isCancelConfirmationPage = isAccountCancelConfirmPage(currentPath)
  const showAiGuide = userLanguage === EN && !isCancelConfirmationPage

  const authJwt = auth.get('jwt')
  const primaryLanguage = userLanguage || EN
  const { data, loading } = useQuery(GET_RECOMMENDED, {
    variables: {
      scenario: 'cancel-recommended-for-you-web',
      language: primaryLanguage,
      contentLanguages: [primaryLanguage],
      imageType: CONTENT_IMAGE_LIST,
      limit: RECOMMENDED_TILE_LIMIT,
    },
    context: {
      jwt: authJwt,
    },
  })
  const items = _get(data, ['recommendationList', 'items'], []) || []
  // create Immutable data for the underlying components
  // Filter out any null/undefined items and ensure all items are converted to Maps
  const immutableContentList = (fromJS(items) || List())
    .filter((item) => item != null && Map.isMap(item))
  // Stub maps (cf. TileV2GraphqlManager) → TileV2Placeholder until recommendations load
  const placeholderContentList = List(
    _times(
      RECOMMENDED_TILE_LIMIT,
      (i) => Map({ titleGroupId: `account-cancel-recommended-ph-${i}` }),
    ),
  )
  const rowContentList = loading ? placeholderContentList : immutableContentList

  const onClickSeeWhatIsComing = () => {
    setEventDefault(CANCEL_FLOW_COMING_SOON_CLICKED_GA4_EVENT)
  }

  const renderItem = (item, params) => {
    // Ensure item is valid before processing
    if (!item) {
      return null
    }

    const tileData = getFormattedContentData(item)

    if (!Map.isMap(tileData)) {
      return null
    }

    return (
      <TileV2Graphql
        key={params.index}
        data={tileData}
        upstreamContext={updateUpstreamContextFromObject(
          upstreamContext,
          {
            contentType: tileData.get('contentType'),
            itemIndex: params.index,
            contentId: tileData.get('contentId'),
          },
        )}
        uiLocation="Account Cancel Dissatisfied"
        uiElement="Recommended Tile"
      />
    )
  }

  return (
    <div className="account-cancel-dissatisfied">
      <H3 className="account-cancel-dissatisfied__title" as={HEADING_TYPES.H4_DESKTOP}>{staticText.getIn(['data', 'sorryYouHaveNotFoundAnything'])}</H3>
      <p className="account-cancel-dissatisfied__subtitle">{staticText.getIn(['data', 'sorryYouHaveNotFoundAnything2'])}</p>
      <div className="account-cancel-dissatisfied__row-container">
        <RowV3
          style={STYLES.S12345}
          onRenderContent={null}
          onVisibilityChange={null}
          isContinueWatchingV3={false}
        >
          {rowContentList.map((item, index) => renderItem(item, { index }))}
        </RowV3>
      </div>
      <div className="account-cancel-dissatisfied__highlight-wrapper">
        {showAiGuide && (
          <AccountCancelDissatisfiedAiGuide
            staticText={staticText}
            oraclePersonalityId={oraclePersonalityId}
            setAiGa4Event={setAiGa4Event}
            setEventDefault={setEventDefault}
          />
        )}
        <div className="account-cancel-dissatisfied__highlight-message-container">
          <H5 className="account-cancel-dissatisfied__highlight-message-title">{staticText.getIn(['data', 'newContent'])}</H5>
          <p className="account-cancel-dissatisfied__highlight-message-content">
            {staticText.getIn(['data', 'contentYouWillLove2'])}
          </p>
          <Button
            type={TYPES.PRIMARY}
            onClick={onClickSeeWhatIsComing}
            className="account-cancel-dissatisfied__highlight-message-button"
            url={comingSoonLink}
          >
            {staticText.getIn(['data', 'seeWhatIsComing'])}
            <ChevronRightIcon />
          </Button>
        </div>
      </div>
    </div>
  )
}

function AccountCancelDissatisfied (props) {
  const { useLegacyLayout = false, ...rest } = props

  if (useLegacyLayout) {
    return <AccountCancelDissatisfiedLegacy {...rest} />
  }

  return <AccountCancelDissatisfiedEnhanced {...rest} />
}

AccountCancelDissatisfied.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  userLanguage: PropTypes.string,
  setEventDefault: PropTypes.func.isRequired,
  setAiGa4Event: PropTypes.func.isRequired,
  useLegacyLayout: PropTypes.bool,
}

AccountCancelDissatisfied.defaultProps = {
  useLegacyLayout: false,
}

export default compose(
  connectStaticText({ storeKey: 'accountCancelDissatisfied' }),
  connectRedux(
    (state) => ({
      auth: state.auth,
      userAccount: state.userAccount,
      userLanguage: getPrimary(state.user.getIn(['data', 'language'], List())),
      oraclePersonalityId: state.oracle.get('persona'),
      currentPath: state.resolver.get('path', ''),
      upstreamContext: state.upstreamContext,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setEventDefault: actions.eventTracking.setEventDefault,
        setAiGa4Event: actions.eventTracking.setAiGa4Event,
      }
    },
  ),
)(AccountCancelDissatisfied)
